import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * The app's colour theme.
 *
 * Three settings, not two: `system` follows the operating system and keeps
 * following it, which is the right default — someone who has told their OS they
 * prefer dark should not have to tell every app as well. `light` and `dark` are
 * explicit choices that hold until changed.
 *
 * The resolved theme is written to `<html data-theme>`, which is what tokens.css
 * keys its dark values off. On the document element rather than a React node so
 * that portalled popovers, and the scrollbars `color-scheme` drives, are themed
 * too.
 */

export type ThemeSetting = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'theme';
const SETTINGS: readonly ThemeSetting[] = ['light', 'dark', 'system'];

const isSetting = (value: unknown): value is ThemeSetting =>
  typeof value === 'string' && (SETTINGS as readonly string[]).includes(value);

interface ThemeContextValue {
  setting: ThemeSetting;
  theme: ResolvedTheme;
  setSetting: (setting: ThemeSetting) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const darkQuery = (): MediaQueryList | null =>
  typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null;

const readSetting = (): ThemeSetting => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isSetting(stored) ? stored : 'system';
  } catch {
    // Private mode and blocked storage both throw. A theme is a preference, not
    // state the app needs, so it falls back to the default.
    return 'system';
  }
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [setting, setSettingState] = useState<ThemeSetting>(readSetting);
  const [systemIsDark, setSystemIsDark] = useState<boolean>(() => darkQuery()?.matches ?? false);

  // The listener stays attached whatever the setting is: the OS can change under
  // an explicit choice too, and the value has to be right the moment the reader
  // switches back to `system`.
  useEffect(() => {
    const query = darkQuery();
    if (!query) return undefined;

    const onChange = (event: MediaQueryListEvent) => setSystemIsDark(event.matches);
    query.addEventListener('change', onChange);

    return () => query.removeEventListener('change', onChange);
  }, []);

  const theme: ResolvedTheme = setting === 'system' ? (systemIsDark ? 'dark' : 'light') : setting;

  // Keeps the attribute in step with later changes. The FIRST value is stamped by
  // the inline script in index.html, before this component exists — an effect
  // runs after the first paint, so relying on it alone showed every dark-mode
  // reader a white page for a frame.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const setSetting = useCallback((next: ThemeSetting) => {
    setSettingState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // The theme still applies for this session.
    }
  }, []);

  // Acts on what the reader can SEE, not on the stored setting: from a
  // system-dark page, "toggle" means "give me light", explicitly, from now on.
  const toggle = useCallback(() => {
    setSetting(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setSetting]);

  const value = useMemo<ThemeContextValue>(
    () => ({ setting, theme, setSetting, toggle }),
    [setting, theme, setSetting, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);

  if (!context) {
    // A thrown error beats a silently inert toggle: the provider is missing, and
    // no amount of clicking will make it work.
    throw new Error('useTheme must be used inside <ThemeProvider>');
  }

  return context;
};
